import React, { useState } from 'react';
import BlazeInputReactSelect from 'blaze-input-react-select';
import { Col, Row, Button } from 'react-bootstrap';
import tinyColor from 'tinycolor2';
import MiniImage from './MiniImage';
import TagCreateForm from './TagCreateForm';
import { bytesToSize } from '../../util/form';
import config from '../../config';
import ApiService from '../../services/ApiService';
import UserBadge from '../chat/UserBadge';
import Nowrap from '../Nowrap';
import { modal } from '../modal/ModalEmitter';
import ManageTags from './ManageTags';
import classNames from 'classnames';

const BottomPanel = ({ attachment = {}, tags = [], closePanel, updateTags, fetchTags, onSetPrimary, onSetPublic }) => {
  const [addMode, setAddMode] = useState(tags.length === 0);
  const [selectKey, setSelectKey] = useState(0);
  const src = `${config.serverUrl}/api/attachment/${attachment.key}/thumbnail?resize=120x`;

  const handleTagSelect = (name, tagId) => {
    return ApiService.put(`attachment/${attachment.id}/tag/${tagId}`).then(response => {
      updateTags(attachment.id, response.data);
      setSelectKey(current => current + 1);
    });
  };

  const removeTag = tagId => {
    return ApiService.delete(`attachment/${attachment.id}/tag/${tagId}`).then(response => {
      updateTags(attachment.id, response.data);
    });
  };

  const addCallback = newTagId => {
    setAddMode(false);
    fetchTags();

    // Auto Add
    handleTagSelect('', newTagId);
  };

  const getTagOptions = () => {
    if (attachment.tags && attachment.tags.length) {
      const tagIds = attachment.tags.map(att => att.tagId);
      const filtered = tags.filter(t => {
        if (tagIds.includes(t.id)) {
          return false;
        }
        return true;
      });

      return filtered.map(t => ({ label: t.label, value: t.id }));
    }

    return tags.map(t => ({ label: t.label, value: t.id }));
  };

  const handleManageClick = () => {
    modal.open({
      hidePrint: true,
      hideFooter: true,
      title: 'Manage Tags',
      style: 'center',
      size: 'md',
      message: <ManageTags callback={fetchTags} />,
    });
  };

  const renderTags = () => {
    if (!attachment.tags || !attachment.tags.length) {
      return (
        <div className="loading-wrapper mt-1" style={{ height: 'initial' }}>
          <div className="loading-child">No Tags Set</div>
        </div>
      );
    }

    return (
      <div className="att-tag-list d-flex">
        {attachment.tags.map(at => {
          const tag = tags.find(t => t.id === at.tagId);

          if (!tag) {
            return null;
          }

          const bg = tag.color || '#ccc';
          const fg = tinyColor(bg).isDark() ? '#fff' : '#000';

          return (
            <div className="general-tag-wrapper" key={tag.id}>
              <div className="general-tag" style={{ backgroundColor: tag.color || '#ccc', color: fg }}>
                <div className="label">{tag.label}</div>
                <i className="fad fa-times-circle" onClick={() => removeTag(tag.id)} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const hasAttachment = attachment && !!attachment.key;

  if (!hasAttachment) {
    return <div className="bottom-panel" />;
  }

  return (
    <div className="bottom-panel">
      <Row className="attachment-details m-0">
        <div className="toolbar-button" onClick={closePanel}>
          <i className="fas fa-caret-down" />
        </div>
        <Col md={7} className="attachment-details-left">
          <div className="thumbnail">
            <MiniImage src={src} placeholder={src} />
          </div>
          <div>
            <div className="attachment-meta">
              <Col md={12}>
                <div className="filename">
                  <label>Filename</label> {attachment.filename}
                </div>
              </Col>
              <Col md={6}>
                <div>
                  <label>Type</label> {attachment.mimeType}
                </div>
                <div>
                  <label>Size</label> {bytesToSize(attachment.size)}
                </div>
                <div>
                  <label>Dimensions</label> {attachment.width} x {attachment.height}
                </div>
              </Col>
              <Col md={6}>
                <div>
                  <label>Upload Date</label> <Nowrap>{new Date(attachment.createdAt).toDateString()}</Nowrap>
                </div>
                <div className="pb-1">
                  <label className="mr-1">By</label> <UserBadge userId={attachment.createdBy} />
                </div>
              </Col>
            </div>
            <div className="attachment-actions">
              <Button
                className={classNames('primary-btn mr-1', { checked: !!attachment.isPrimary })}
                size="md"
                variant="secondary"
                style={{ boxShadow: 'none' }}
                onClick={() => onSetPrimary(attachment)}
              >
                <i className="fad fa-star" />
                <i className="fas fa-check-circle check" />
              </Button>
              <Button
                size="md"
                variant="secondary"
                style={{ boxShadow: 'none' }}
                className={classNames('primary-btn mr-1', { checked: attachment.isPublic === true })}
                onClick={() => onSetPublic(attachment.id)}
              >
                <i className="fas fa-globe" />
                <i className="fas fa-check-circle check" />
              </Button>
            </div>
          </div>
        </Col>
        <Col md={5} className="attachment-details-right">
          {addMode === true ? (
            <TagCreateForm callback={addCallback} onCancel={() => setAddMode(false)} hideCancel={tags.length === 0} />
          ) : (
            <div className="select-tag">
              <BlazeInputReactSelect
                key={selectKey}
                className="react-select-container flex-grow-1 mb-0"
                classNamePrefix="react-select"
                onChange={handleTagSelect}
                controlId="tag-list"
                placeholder="Add Existing Tag"
                field={{
                  name: 'tag',
                  label: '',
                  options: getTagOptions(),
                }}
              />
              <Button variant="success" className="ml-2" onClick={() => setAddMode(true)}>
                New Tag
              </Button>
              <Button variant="secondary" className="ml-2" onClick={handleManageClick}>
                Manage
              </Button>
            </div>
          )}
          {addMode === false && renderTags()}
        </Col>
      </Row>
    </div>
  );
};

export default BottomPanel;
